import { useState, useEffect } from 'react';
import axios from 'axios';
import FeeI from '../interfaces/Fee';
import GuestI from '../interfaces/Guest';
import AllAccessGuestI from '../interfaces/AllAccessGuest';

const clampPoints = (points?: number) => {
    if (!points || points < 0) return 0;
    if (points > 5) return 5;
    return points;
};

/**
 * For All Access guests this hook delegates to /api/pink-fee/preview —
 * the SINGLE SOURCE OF TRUTH for fee computation. The server resolves:
 *   • business-day day-of-week (NOT wall-clock — Sat 1am is Friday)
 *   • current weekly point total (distinct AAEventFee business-days)
 *   • projected points (current + 1 if today is a NEW business day)
 *   • fee tier from the AAEventFee table at (businessDow, projectedTier)
 *   • OtherFee rebate (time-tier discount applied server-side)
 *   • finalValue = max(0, feeValue - discountValue)
 *
 * The hook returns finalValue directly — no client-side discount math.
 *
 * For limitedAccess we fall back to the legacy /api/fees/:day endpoint
 * and the local useDiscount hook (which the caller passes in via the
 * `discount` field on the guest argument).
 */
const useFee = (
    type: string,
    { discount, points, guestID }: GuestI | AllAccessGuestI,
    day: number,
    today: string = 'none'
) => {
    const [fee, setFee] = useState<FeeI | null>(null);
    const [loadingFee, setLoading] = useState(true);
    const [guestPoints, setGuestPoints] = useState(clampPoints(points));

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);

                // Match BOTH the fee-type string ('AAEventFee') and
                // the guest-type string ('allAccess'). Callers in
                // production pass feeType — the prior check missed
                // every All Access guest, so pricingType / specialPricing
                // were silently ignored on the kiosk path.
                if (
                    (type === 'AAEventFee' || type === 'allAccess') &&
                    guestID
                ) {
                    // Single-source-of-truth path — server applies
                    // pricingType (vip/special/comp), special-pricing
                    // base fee, and per-tier surcharge gating. Return
                    // finalValue as-is.
                    // eslint-disable-next-line no-console
                    console.log('[ALLACCESS KIOSK FEE REQUEST]', {
                        guestID,
                        feeType: type,
                        callDay: day,
                        callToday: today,
                    });
                    const { data } = await axios.get(
                        `/api/pink-fee/preview?guestId=${encodeURIComponent(guestID)}`
                    );
                    // eslint-disable-next-line no-console
                    console.log('[ALLACCESS KIOSK FEE RESPONSE]', {
                        guestID,
                        pricingType: data?.pricingType,
                        specialPricingApplied: data?.specialPricingApplied,
                        baseFee: data?.baseFee,
                        regularBaseFeeWouldHaveBeen:
                            data?.regularBaseFeeWouldHaveBeen,
                        surchargeTotal: data?.surchargeTotal,
                        finalValue: data?.finalValue,
                    });
                    setGuestPoints(data.projectedPoints);
                    setFee({
                        value: data.finalValue,
                        _id: data.feeId,
                        day: data.businessDayOfWeek,
                        name: today,
                    });
                    return;
                }

                // ── limitedAccess legacy path ──
                const safePoints = clampPoints(guestPoints);
                const { data } = await axios.get(
                    `/api/fees/${day}?type=${type}&points=${safePoints}`
                );

                if (today === 'none') {
                    return setFee({
                        value: data.value,
                        _id: data._id,
                        day: day,
                        name: today,
                    });
                }

                let total = data.value;
                if (typeof discount === 'number' && discount > 0) {
                    // discount = REBATE (subtract). Confirmed semantic
                    // 2026-05-02; persist path now matches.
                    total = data.value - discount;
                    if (total < 0) total = 0;
                }
                setFee({ value: total, day, name: today });
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guestPoints, guestID, type]);

    return {
        fee,
        setFee,
        loadingFee,
        setGuestPoints,
        setLoading,
    };
};

export default useFee;
